import OrbitalMesh from '../view/OrbitalMesh';
import Side from '../model/side';
import Configuration from '../model/Configuration';
import Params from '../options/Params';
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Axis, Space } from "@babylonjs/core/Maths/math.axis";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Tools } from "@babylonjs/core/Misc/tools";
import "@babylonjs/loaders/glTF/2.0/glTFLoader";

import piGlb from '../../meshes/pi.glb';
import povoidGlb from '../../meshes/povoid.glb';
import sGlb from '../../meshes/s.glb';
import sigmaGlb from '../../meshes/sigma.glb';
import smooshedovoidGlb from '../../meshes/smooshedovoid.glb';


export default class OrbitalFactory {

  constructor(scene) {
    this.scene = scene;
    this.baseMeshes = {};
    this.meshMap = {
      pi: piGlb,
      povoid: povoidGlb,
      s: sGlb,
      sigma: sigmaGlb,
      smooshedovoid: smooshedovoidGlb,
    }
  }

  load() {
    const promises = [];
    const ids = Object.keys(this.meshMap);

    ids.forEach(key => {
      const promise = this.loadMesh(key, this.meshMap[key]);
      promises.push(promise);
    });

    return Promise.all(promises);
  }

  loadMesh(id, file) {
    return new Promise((resolve, reject) => {
      const raw_content = Tools.DecodeBase64(file);
      const blob = new Blob([raw_content]);
      const url = URL.createObjectURL(blob);

      SceneLoader.Append("", url, this.scene, () => {
        this.baseMeshes[id] = this.scene.meshes.find(x => x.name == id);
        this.scene.meshes.forEach(mesh => mesh.isVisible = false);

        resolve();
      }, () => {
        // on progress
      }, () => {
        console.error(`Failed to load ${id}`);
      }, ".glb");
    });
  }

  createSigma() {
    const mesh = new OrbitalMesh("sigma", this.scene);

    const left = this.baseMeshes["smooshedovoid"].clone();
    const center = this.baseMeshes["sigma"].clone();
    const right = this.baseMeshes["smooshedovoid"].clone();

    left.position.x = -1.4;
    right.position.x = 1.4;

    left.scaling = new Vector3(0.2, -0.2, 0.2);
    center.scaling = new Vector3(0.54, 0.54, 0.54);
    right.scaling = new Vector3(0.2, 0.2, 0.2);

    left.parent = mesh;
    center.parent = mesh;
    right.parent = mesh;

    center.material.alpha = Params.theme == "paper" ? 0.8 : 0.5;
    center.material.albedoColor = new Color3(0, 0.8, 0.3);
    mesh.geometricMeshes = [left, center, right];

    return mesh;
  }


  createPi() {
    const mesh = new OrbitalMesh("pi", this.scene);
    const bottomHalf = this.baseMeshes["pi"].clone();
    const topHalf = this.baseMeshes["pi"].clone();

    topHalf.rotate(Axis.X, Math.PI, Space.WORLD);

    topHalf.position.y = 1;
    bottomHalf.position.y = -1;

    topHalf.scaling = new Vector3(0.5, 0.75, 0.5);
    bottomHalf.scaling = new Vector3(0.5, 0.75, 0.5);

    bottomHalf.parent = mesh;
    topHalf.parent = mesh;

    if (Params.theme == "paper") {
      topHalf.material.alpha = 0.75;
      bottomHalf.material.alpha = 0.75;
    }

    mesh.geometricMeshes = [bottomHalf, topHalf];
    mesh.geometricMeshes.forEach(mesh => mesh.material.albedoColor = new Color3(0, 0.25, 0.8));

    return mesh;
  }

  createP() {
    const p = this.createPOrbital();
    const mesh = this.createMesh("p", p);
    mesh.geometricMeshes.forEach(mesh => mesh.material.albedoColor = new Color3(0, 0.25, 0.8));
    return mesh;
  }

  createD() {
    var material = new StandardMaterial(this.scene);
    material.alpha = Params.theme == "paper" ? 0.7 : 0.35;
    material.diffuseColor = new Color3(1.0, 0.2, 0.7);
    material.specularColor = new Color3(0.1, 0.1, 0.1);

    const d1 = this.createPOrbital();
    d1[0].rotateAround(new Vector3.Zero(), Axis.Z, 45 * (Math.PI / 180));
    d1[1].rotateAround(new Vector3.Zero(), Axis.Z, 45 * (Math.PI / 180));
    d1[0].material = material;
    d1[1].material = material;

    const d2 = this.createPOrbital();
    d2[0].rotateAround(new Vector3.Zero(), Axis.Z, 135 * (Math.PI / 180));
    d2[1].rotateAround(new Vector3.Zero(), Axis.Z, 135 * (Math.PI / 180));
    d2[0].material = material;
    d2[1].material = material;

    const mesh = this.createMesh("d", [...d1, ...d2]);
    return mesh;
  }

  createS() {
    const s = this.baseMeshes["s"].clone();
    s.scaling = new Vector3(0.75, 0.75, .75);
    if (Params.theme == "paper") {
      s.material.alpha = 0.8;
    }
    const mesh = this.createMesh('s', [s]);

    return mesh;
  }

  createSP(side) {
    const left = this.createHybridizedOrbital();
    left[1].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const right = this.createHybridizedOrbital();
    right[0].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    let name;
    let subs;
    if (side === Side.RIGHT) {
      name = "sp-r";
      subs = [...right, ...left];
    } else {
      name = "sp-l";
      subs = [...left, ...right];
    }

    const mesh = this.createMesh(name, subs);
    mesh.hybridization = Configuration.sp;
    return mesh;
  }

  createSP2(side) {
    if (side === Side.RIGHT) {
      return this.createSP2Right();
    } else {
      return this.createSP2Left();
    }
  }

  createSP2Left() {
    const left = this.createHybridizedOrbital();
    left[1].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Z, 120 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Z, 300 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Z, 240 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Z, 420 * (Math.PI / 180));

    const mesh = this.createMesh("sp2-l", [...left, ...eq1, ...eq2]);
    mesh.hybridization = Configuration.sp2;
    return mesh;
  }

  createSP2Right() {
    const right = this.createHybridizedOrbital();
    right[0].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Z, 60 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Z, 240 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Z, 300 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Z, 480 * (Math.PI / 180));

    const mesh = this.createMesh("sp2-r", [...right, ...eq1, ...eq2]);
    mesh.hybridization = Configuration.sp2;
    return mesh;
  }


  createSP3(side) {
    if (side === Side.RIGHT) {
      return this.createSP3Right();
    } else {
      return this.createSP3Left();
    }
  }

  createSP3Left() {
    const left = this.createHybridizedOrbital();
    left[1].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Z, 120 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Z, 300 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Z, 240 * (Math.PI / 180));
    eq2[0].rotateAround(new Vector3.Zero(), Axis.X, 60 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Z, 420 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.X, 60 * (Math.PI / 180));

    const eq3 = this.createHybridizedOrbital();
    eq3[0].rotateAround(new Vector3.Zero(), Axis.Z, 240 * (Math.PI / 180));
    eq3[0].rotateAround(new Vector3.Zero(), Axis.X, -60 * (Math.PI / 180));
    eq3[1].rotateAround(new Vector3.Zero(), Axis.Z, 420 * (Math.PI / 180));
    eq3[1].rotateAround(new Vector3.Zero(), Axis.X, -60 * (Math.PI / 180));

    const mesh = this.createMesh("sp3-l", [...left, ...eq1, ...eq2, ...eq3]);
    mesh.hybridization = Configuration.sp3;
    return mesh;
  }

  createSP3Right() {
    const right = this.createHybridizedOrbital();
    right[0].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Z, 60 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Z, 240 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Z, 300 * (Math.PI / 180));
    eq2[0].rotateAround(new Vector3.Zero(), Axis.X, 60 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Z, 480 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.X, 60 * (Math.PI / 180));

    const eq3 = this.createHybridizedOrbital();
    eq3[0].rotateAround(new Vector3.Zero(), Axis.Z, 300 * (Math.PI / 180));
    eq3[0].rotateAround(new Vector3.Zero(), Axis.X, -60 * (Math.PI / 180));
    eq3[1].rotateAround(new Vector3.Zero(), Axis.Z, 480 * (Math.PI / 180));
    eq3[1].rotateAround(new Vector3.Zero(), Axis.X, -60 * (Math.PI / 180));

    const mesh = this.createMesh("sp3-r", [...right, ...eq1, ...eq2, ...eq3]);
    mesh.hybridization = Configuration.sp3;
    return mesh;
  }

  createSP3D(side) {
    if (side === Side.RIGHT) {
      return this.createSP3DRight();
    } else {
      return this.createSP3DLeft();
    }
  }

  createSP3DLeft() {
    const left = this.createHybridizedOrbital();
    left[1].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Y, 120 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Y, 300 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Y, 240 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Y, 60 * (Math.PI / 180));

    const ax1 = this.createHybridizedOrbital();
    ax1[0].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));
    ax1[1].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));

    const ax2 = this.createHybridizedOrbital();
    ax2[0].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));
    ax2[1].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));

    const mesh = this.createMesh("sp3d-l", [...left, ...eq1, ...eq2, ...ax1, ...ax2]);
    mesh.hybridization = Configuration.sp3d;
    return mesh;
  }

  createSP3DRight() {
    const right = this.createHybridizedOrbital();
    right[0].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Y, 60 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Y, 240 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Y, 300 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Y, 120 * (Math.PI / 180));

    const ax1 = this.createHybridizedOrbital();
    ax1[0].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));
    ax1[1].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));

    const ax2 = this.createHybridizedOrbital();
    ax2[0].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));
    ax2[1].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));

    const mesh = this.createMesh("sp3d-r", [...right, ...eq1, ...eq2, ...ax1, ...ax2]);
    mesh.hybridization = Configuration.sp3d;
    return mesh;
  }

  createSP3D2(side) {
    if (side === Side.RIGHT) {
      return this.createSP3D2Right();
    } else {
      return this.createSP3D2Left();
    }
  }

  createSP3D2Left() {
    const left = this.createHybridizedOrbital();
    left[1].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Y, 90 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Y, 270 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Y, 180 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Y, 0 * (Math.PI / 180));

    const eq3 = this.createHybridizedOrbital();
    eq3[0].rotateAround(new Vector3.Zero(), Axis.Y, 270 * (Math.PI / 180));
    eq3[1].rotateAround(new Vector3.Zero(), Axis.Y, 90 * (Math.PI / 180));

    const ax1 = this.createHybridizedOrbital();
    ax1[0].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));
    ax1[1].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));

    const ax2 = this.createHybridizedOrbital();
    ax2[0].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));
    ax2[1].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));

    const mesh = this.createMesh("sp3d2-l", [...left, ...eq1, ...eq2, ...eq3, ...ax1, ...ax2]);
    mesh.hybridization = Configuration.sp3d2;
    return mesh;
  }

  createSP3D2Right() {
    const right = this.createHybridizedOrbital();
    right[0].rotateAround(new Vector3.Zero(), Axis.Z, Math.PI);

    const eq1 = this.createHybridizedOrbital();
    eq1[0].rotateAround(new Vector3.Zero(), Axis.Y, 90 * (Math.PI / 180));
    eq1[1].rotateAround(new Vector3.Zero(), Axis.Y, 270 * (Math.PI / 180));

    const eq2 = this.createHybridizedOrbital();
    eq2[0].rotateAround(new Vector3.Zero(), Axis.Y, 0 * (Math.PI / 180));
    eq2[1].rotateAround(new Vector3.Zero(), Axis.Y, 180 * (Math.PI / 180));

    const eq3 = this.createHybridizedOrbital();
    eq3[0].rotateAround(new Vector3.Zero(), Axis.Y, 270 * (Math.PI / 180));
    eq3[1].rotateAround(new Vector3.Zero(), Axis.Y, 90 * (Math.PI / 180));

    const ax1 = this.createHybridizedOrbital();
    ax1[0].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));
    ax1[1].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));

    const ax2 = this.createHybridizedOrbital();
    ax2[0].rotateAround(new Vector3.Zero(), Axis.Z, 270 * (Math.PI / 180));
    ax2[1].rotateAround(new Vector3.Zero(), Axis.Z, 90 * (Math.PI / 180));

    const mesh = this.createMesh("sp3d2-r", [...right, ...eq1, ...eq2, ...eq3, ...ax1, ...ax2]);
    mesh.hybridization = Configuration.sp3d2;
    return mesh;
  }


  createPOrbital() {
    const left = this.baseMeshes["povoid"].clone();
    left.position.x = .765;
    left.scaling = new Vector3(0.5, 0.5, 0.5);
    const right = this.baseMeshes["povoid"].clone();
    right.position.x = -.765;
    right.scaling = new Vector3(0.5, 0.5, 0.5);
    right.rotate(Axis.Z, Math.PI, Space.LOCAL);

    if (Params.theme == "paper") {
      left.material.alpha = 0.75;
      right.material.alpha = 0.75;
    }

    return [left, right];
  }

  createHybridizedOrbital() {
    const scale = new Vector3(0.5, 0.5, 0.5);
    const scaleMinor = new Vector3(0.2, 0.2, 0.2);

    const major = this.baseMeshes["smooshedovoid"].clone();
    major.position = new Vector3.Zero();
    major.translate(Axis.Y, -1, Space.World);
    major.scaling.copyFrom(scale);

    const minor = this.baseMeshes['smooshedovoid'].clone();
    minor.position = new Vector3.Zero();
    minor.translate(Axis.Y, -.5, Space.World);
    minor.scaling.copyFrom(scaleMinor);

    if (Params.theme == "paper") {
      major.material.alpha = 0.8;
      minor.material.alpha = 0.8;
    }

    return [major, minor];

  }

  createMesh(name, submeshes) {
    const mesh = new OrbitalMesh(name, this.scene);
    mesh.geometricMeshes = submeshes;
    mesh.geometricMeshes.forEach(subMesh => subMesh.parent = mesh);
    return mesh;
  }



}